import React, { useState, useMemo, useRef, useCallback } from 'react';
import PageToolbar from '../util/PageToolbar';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { t } from 'i18next';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import AddIcon from '@mui/icons-material/Add';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyCheckDollar, faPaperclip, faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import DataTable from '../util/DataTable';
import { ColDef, BulkEditField } from '../util/DataTable';
import UserAvatar from '../util/UserAvatar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from '../util/SearchFilter';
import SidePanel from '../util/SidePanel';

interface Props {
  rows: any[];
  fields: any[];
  access: any;
  staticDateCompany: any;
  baseUrl: any;
  onAdd: () => void;
  onEdit: (row: any) => void;
  refresh: () => void;
}

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  pending:   { color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  rejected:  { color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  fulfilled: { color: '#3b82f6', bg: 'rgba(59,130,246,.12)' },
  paid:      { color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
};

const STATUS_MENU_ITEMS = [
  { id: 1, title: 'pending' },
  { id: 2, title: 'rejected' },
  { id: 3, title: 'approved' },
];

export default function FinanceExpensesTable({
  rows, fields, access, staticDateCompany, baseUrl,
  onAdd, onEdit, refresh,
}: Props) {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [pendingRejectIds, setPendingRejectIds] = useState<number[]>([]);
  const [attachUrl, setAttachUrl] = useState<string | null>(null);
  const [attachName, setAttachName] = useState('');
  const [zipping, setZipping] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const openAttachment = useCallback((r: any) => {
    const url = `${baseUrl}${(r.payment_document_url || '').replace(/^\//, '')}`;
    setAttachName(r.payment_document_url?.split('/').pop() || 'attachment');
    setAttachUrl(url);
  }, [baseUrl]);

  const shownRowsRef = useRef<any[]>([]);

  const handleExportZip = async () => {
    const ids = shownRowsRef.current.filter((r: any) => r.payment_document_url).map((r: any) => r.id);
    if (!ids.length) return;
    setZipping(true);
    try {
      const resp = await axios.post('expenses/export-zip', { ids }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attachments-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('ZIP export failed', { variant: 'error' });
    } finally {
      setZipping(false);
    }
  };

  const notifyStatusError = () =>
    enqueueSnackbar(t('StatusUpdateFailed'), { variant: 'error' });

  const isFieldShown = (column: string) =>
    fields.find((f: any) => f.table_name === 'expenses' && f.column_name === column)?.isShown;

  const userOptions = useMemo(() => {
    const seen = new Set<string>();
    return (rows as any[])
      .reduce((acc: { value: string; label: string }[], r) => {
        if (r.create_user) {
          const name = `${r.create_user.first_name} ${r.create_user.last_name}`;
          if (!seen.has(name)) { seen.add(name); acc.push({ value: name, label: name }); }
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    return (rows as any[]).reduce((acc: { value: string; label: string }[], r) => {
      if (r.categoryTree && !seen.has(r.categoryTree)) { seen.add(r.categoryTree); acc.push({ value: r.categoryTree, label: r.categoryTree }); }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const handleStatusClick = (e: React.MouseEvent<HTMLElement>, rowId: number) => {
    setMenuRowId(rowId);
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuSelect = async (rawTitle: string) => {
    const status = rawTitle === 'approved' ? 'fulfilled' : rawTitle;
    if (status === 'rejected') {
      setPendingRejectIds([menuRowId!]);
      setRejectReason('');
      setRejectDialogOpen(true);
      setMenuAnchor(null);
      return;
    }
    try {
      await axios.put('expenses/update/status', { status, id: [menuRowId] });
      refresh();
    } catch {
      notifyStatusError();
    } finally {
      setMenuAnchor(null);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await axios.put('expenses/update/status', { status: 'fulfilled', id: [id] });
      refresh();
    } catch {
      notifyStatusError();
    }
  };

  const handleRejectConfirm = async () => {
    try {
      await axios.put('expenses/update/status', {
        status: 'rejected',
        id: pendingRejectIds,
        status_comment: rejectReason,
      });
      refresh();
    } catch {
      notifyStatusError();
    } finally {
      setRejectDialogOpen(false);
      setRejectReason('');
      setPendingRejectIds([]);
    }
  };

  const columns = useMemo((): ColDef[] => {
    const cols: ColDef[] = [
      {
        key: 'date', label: t('Date'), sortable: true,
        sortExtract: (r) => r.date ?? '',
        filterType: 'daterange',
        filterExtract: (r) => r.date ?? '',
        render: (r) => r.date ? moment(r.date).format(staticDateCompany) : '—',
      },
      {
        key: 'name', label: t('Name'), defaultVisible: false,
        filterType: 'text',
        filterExtract: (r) => r.name || '',
        render: (r) => r.name || '—',
      },
      {
        key: 'create_user', label: t('NameCreator'),
        filterType: 'select',
        filterOptions: userOptions,
        filterExtract: (r) => r.create_user ? `${r.create_user.first_name} ${r.create_user.last_name}` : '',
        render: (r) => r.create_user ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAvatar user={r.create_user} baseUrl={baseUrl} size={28} />
            <span>{r.create_user.first_name} {r.create_user.last_name}</span>
          </div>
        ) : '—',
      },
    ];

    // Department / Project are always available in the Columns menu; whether they
    // show by default follows the company field config (isFieldShown).
    cols.push(
      {
        key: 'nominal_department', label: t('Department'),
        defaultVisible: !!isFieldShown('nominal_department'),
        render: (r) => r.nominal_department || '—',
      },
      {
        key: 'project', label: t('Project'),
        defaultVisible: !!isFieldShown('project'),
        render: (r) => r.project || '—',
      },
    );

    cols.push(
      {
        key: 'categoryTree', label: t('Category'),
        filterType: 'select',
        filterOptions: categoryOptions,
        filterExtract: (r) => r.categoryTree || '',
        render: (r) => r.categoryTree || '—',
      },
      {
        key: 'supervisor', label: t('Supervisor'),
        render: (r) => r.supervisor
          ? `${r.supervisor.first_name} ${r.supervisor.last_name}`
          : '—',
      },
      {
        key: 'staff', label: t('Staff'), sortable: false,
        render: (r) => {
          const title = r.staff
            ? (r.description || '')
            : [
                r.name && `Name: ${r.name}`,
                r.contact_number && `Phone: ${r.contact_number}`,
                r.description && `Desc: ${r.description}`,
                r.account_number && `Account: ${r.account_number}`,
                r.iban && `IBAN: ${r.iban}`,
                r.finance_additional_info && `Finance: ${r.finance_additional_info}`,
              ].filter(Boolean).join('\n');
          return (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{title}</span>} placement="right">
              {r.staff
                ? <CheckCircleIcon sx={{ fontSize: 18, cursor: 'pointer' }} />
                : <DoNotDisturbOnIcon sx={{ fontSize: 18, cursor: 'pointer' }} />}
            </Tooltip>
          );
        },
      },
      {
        key: 'amount', label: t('Amount'), sortable: true, align: 'right',
        sortExtract: (r) => Number(r.amount) || 0,
        render: (r) => r.amount != null ? Number(r.amount).toFixed(2) : '—',
      },
      {
        key: 'status', label: t('StatusExpense'), sortable: false,
        filterType: 'select',
        filterExtract: (r) => r.status ?? '',
        filterOptions: [
          { value: 'pending',   label: 'Pending' },
          { value: 'rejected',  label: 'Rejected' },
          { value: 'fulfilled', label: 'Approved' },
          { value: 'paid',      label: 'Paid' },
        ],
        render: (r) => {
          const canApprove = access.expenses_admin === true || access.expenses_manager === true;
          const canClick = canApprove && r.status !== 'paid';
          const sc = STATUS_COLORS[r.status];
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tooltip title={r.status === 'rejected' && r.status_comment ? `${t('RejectionReason')}: ${r.status_comment}` : ''}>
                <Chip
                  label={r.status === 'fulfilled' ? 'approved' : (r.status || '')}
                  onClick={canClick ? (e) => handleStatusClick(e, r.id) : undefined}
                  variant="outlined"
                  sx={{
                    height: 26, fontSize: '0.72rem',
                    borderRadius: '20px',
                    color: sc?.color ?? 'var(--text-color)',
                    borderColor: sc?.color ?? 'var(--border-input)',
                    background: sc?.bg ?? 'transparent',
                    cursor: canClick ? 'pointer' : 'default',
                    '& .MuiChip-label': { padding: '0 9px' },
                  }}
                />
              </Tooltip>
              {r.status === 'pending' && canApprove && (
                <>
                  <Tooltip title={t('Approve') as string}>
                    <button
                      type="button"
                      className="fx-ar-btn ok"
                      onClick={() => handleApprove(r.id)}
                    >✓</button>
                  </Tooltip>
                  <Tooltip title={t('Reject') as string}>
                    <button
                      type="button"
                      className="fx-ar-btn no"
                      onClick={() => { setPendingRejectIds([r.id]); setRejectReason(''); setRejectDialogOpen(true); }}
                    >✗</button>
                  </Tooltip>
                </>
              )}
            </div>
          );
        },
      },
      {
        key: 'payment_document_url', label: '📎', sortable: false,
        filterType: 'boolean',
        filterExtract: (r) => r.payment_document_url ? 'true' : 'false',
        render: (r) => r.payment_document_url ? (
          <Tooltip title={r.payment_document_url.split('/').pop()}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openAttachment(r); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '2px 4px', color: '#3b82f6', fontSize: 14,
                display: 'flex', alignItems: 'center',
              }}
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </button>
          </Tooltip>
        ) : <span style={{ color: 'var(--text-color-calendar-week)', fontSize: 11 }}>—</span>,
      },
    );

    return cols;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, access, staticDateCompany, baseUrl, userOptions, categoryOptions]);

  const shownRows = useMemo(() => {
    const result = applySearchAndFilters(rows, search, filters, columns, (r: any) => [
      r.create_user?.first_name, r.create_user?.last_name,
      r.supervisor?.first_name, r.supervisor?.last_name,
      r.categoryTree, r.nominal_department, r.project, r.description, r.name,
    ].filter(Boolean).join(' '));
    shownRowsRef.current = result;
    return result;
  }, [rows, search, filters, columns]);

  const bulkEditFields: BulkEditField[] | undefined =
    (access.expenses_admin || access.expenses_manager) ? [
      {
        key: 'status', label: t('StatusExpense'), type: 'select',
        options: [
          { value: 'pending',   label: 'Pending' },
          { value: 'rejected',  label: 'Rejected' },
          { value: 'fulfilled', label: 'Approved' },
          { value: 'paid',      label: 'Paid' },
        ],
      },
    ] : undefined;

  const handleBulkEdit = async (keys: (string | number)[], field: string, value: any) => {
    if (field === 'status' && value === 'rejected') {
      setPendingRejectIds(keys as number[]);
      setRejectReason('');
      setRejectDialogOpen(true);
      return;
    }
    try {
      await axios.put('expenses/update/status', { status: value, id: keys });
      refresh();
    } catch {
      notifyStatusError();
    }
  };

  const handleBulkDelete = (access.expenses_admin || access.expenses_manager)
    ? async (keys: (string | number)[]) => {
        await Promise.all((keys as number[]).map((id) => axios.delete(`expenses/${id}`)));
        refresh();
      }
    : undefined;

  const toolbarSlot = useRef<HTMLDivElement>(null);

  return (
    <>
      <PageToolbar>
        <SearchFilter grow columns={columns} search={search} onSearch={setSearch} filters={filters} onFilters={setFilters} />
        {(access.expenses_add === true || access.expenses_admin === true || access.expenses_manager === true) && (
          <button type="button" onClick={onAdd} className="btn-primary">
            <AddIcon sx={{ fontSize: 15 }} />
            <span>{t('AddExpenses')}</span>
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="dt-btn-ghost"
          onClick={e => setMoreAnchor(e.currentTarget)}
          style={{ fontSize: 16, lineHeight: 1, padding: '0 6px' }}
          title="More options"
        >⋯</button>
        <Menu
          anchorEl={moreAnchor}
          open={Boolean(moreAnchor)}
          onClose={() => setMoreAnchor(null)}
          MenuListProps={{ dense: true, sx: { py: 0.5 } }}
          slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 160 } } }}
        >
          <MenuItem onClick={() => { setMoreAnchor(null); handleExportZip(); }} disabled={zipping} sx={{ fontSize: 12.5, gap: 1.5, minHeight: 32 }}>
            <FontAwesomeIcon icon={faFileArrowDown} style={{ fontSize: 14, opacity: 0.7 }} /> {zipping ? 'Preparing…' : 'Export ZIP'}
          </MenuItem>
        </Menu>
        <div ref={toolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
      </PageToolbar>
      <div className="section-scroll">
        <DataTable
          rows={shownRows}
          getKey={(r: any) => r.id}
          columns={columns}
          filterable={false}
          onEdit={(r: any) => (r.status !== 'paid' && r.status !== 'fulfilled') ? onEdit(r) : undefined}
          onDeleteMany={handleBulkDelete}
          bulkEditFields={bulkEditFields}
          onBulkEdit={handleBulkEdit}
          defaultPageSize={20}
          storageKey="finance-expenses-cols"
          footerIcon={<FontAwesomeIcon icon={faMoneyCheckDollar} className="text-[16px] opacity-30 text-[var(--text-color)]" />}
          toolbarRef={toolbarSlot}
        />
      </div>

      {/* Status change menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}>
        {STATUS_MENU_ITEMS.map((s) => (
          <MenuItem key={s.id} onClick={() => handleMenuSelect(s.title)}>
            {s.title}
          </MenuItem>
        ))}
      </Menu>

      {/* Reject reason dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>{t('RejectionReason')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus multiline rows={3} fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('RejectionReasonPlaceholder')}
            sx={{ mt: 1, minWidth: 320 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained" color="error"
            disabled={!rejectReason.trim()}
            onClick={handleRejectConfirm}>
            {t('Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attachment viewer panel */}
      <SidePanel open={!!attachUrl} onClose={() => setAttachUrl(null)} width={560}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border2-new)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FontAwesomeIcon icon={faPaperclip} style={{ color: '#3b82f6' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {attachName}
            </span>
            <a
              href={attachUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              download
              style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}
            >
              ↓ {t('Download') || 'Download'}
            </a>
          </div>
          <div style={{ flex: 1, overflow: 'hidden', background: 'var(--bg-top)' }}>
            {attachUrl && (
              /\.(jpg|jpeg|png|gif|webp)$/i.test(attachUrl) ? (
                <img src={attachUrl} alt={attachName} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 16 }} />
              ) : (
                <iframe src={attachUrl} title={attachName} style={{ width: '100%', height: '100%', border: 'none' }} />
              )
            )}
          </div>
        </div>
      </SidePanel>
    </>
  );
}
